import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '../../context/PlatformAuthContext';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Settings, Check, X, Building2 } from 'lucide-react';

const PlatformModuleConfig = () => {
  const { apiFetch } = usePlatformAuth();
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const [modules, setModules] = useState({});
  const [loadingTenants, setLoadingTenants] = useState(true);
  const [loadingModules, setLoadingModules] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchTenants = async () => {
      try {
        const res = await apiFetch('/tenants');
        setTenants(res.data.tenants || []);
      } catch { toast.error('Failed to load schools'); }
      setLoadingTenants(false);
    };
    fetchTenants();
  }, []);

  const loadModules = async (tenantId) => {
    if (!tenantId) return;
    setSelectedTenant(tenantId);
    setLoadingModules(true);
    try {
      const res = await apiFetch(`/tenants/${tenantId}/modules`);
      setModules(res.data.modules || {});
      setTenantInfo(res.data.tenant || null);
    } catch { toast.error('Failed to load modules'); }
    setLoadingModules(false);
  };

  const toggle = (key) => {
    setModules(prev => ({
      ...prev,
      [key]: { ...prev[key], enabled: !prev[key].enabled },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch(`/tenants/${selectedTenant}/modules`, {
        method: 'PUT',
        body: JSON.stringify({ modules }),
      });
      toast.success('Module configuration saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  return (
    <div data-testid="platform-module-config">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-white mb-1" data-testid="platform-module-config-title">Feature Control</h1>
        <p className="text-slate-400">Manage which ERP modules are enabled for each school</p>
      </div>

      {/* School selector */}
      <div className="mb-8 max-w-md">
        <label className="text-sm font-medium text-slate-300 mb-2 block">Select School</label>
        <Select value={selectedTenant} onValueChange={loadModules} disabled={loadingTenants}>
          <SelectTrigger className="bg-slate-900 border-slate-700 text-white h-12" data-testid="module-school-select">
            <SelectValue placeholder={loadingTenants ? 'Loading schools...' : 'Choose a school'} />
          </SelectTrigger>
          <SelectContent>
            {tenants.map(t => (
              <SelectItem key={t.tenant_id} value={t.tenant_id}>
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-indigo-400" />
                  {t.name}
                  {t.school_code && <span className="text-xs font-mono text-slate-400 ml-1">({t.school_code})</span>}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Module grid */}
      {loadingModules ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin h-8 w-8 border-2 border-indigo-500 border-t-transparent rounded-full" />
        </div>
      ) : selectedTenant && Object.keys(modules).length > 0 ? (
        <>
          {tenantInfo && (
            <div className="flex items-center gap-3 mb-6 p-4 bg-slate-900/50 border border-slate-800 rounded-xl">
              <div className="h-10 w-10 rounded-xl bg-indigo-600 flex items-center justify-center text-white font-bold">
                {tenantInfo.name?.charAt(0) || 'S'}
              </div>
              <div>
                <p className="text-white font-semibold">{tenantInfo.name}</p>
                {tenantInfo.school_code && <p className="text-xs font-mono text-slate-400 tracking-wider">{tenantInfo.school_code}</p>}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {Object.entries(modules).map(([key, mod]) => (
              <div
                key={key}
                className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${mod.enabled ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-slate-800 bg-slate-900/50 opacity-60'}`}
                onClick={() => toggle(key)}
                data-testid={`platform-module-toggle-${key}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${mod.enabled ? 'bg-indigo-500/20' : 'bg-slate-800'}`}>
                      <Settings className={`h-5 w-5 ${mod.enabled ? 'text-indigo-400' : 'text-slate-500'}`} />
                    </div>
                    <div>
                      <p className={`font-semibold ${mod.enabled ? 'text-white' : 'text-slate-400'}`}>{mod.label}</p>
                      <p className="text-xs text-slate-500 capitalize">{key.replace(/_/g, ' ')}</p>
                    </div>
                  </div>
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center ${mod.enabled ? 'bg-indigo-500' : 'bg-slate-700'}`}>
                    {mod.enabled ? <Check className="h-3.5 w-3.5 text-white" /> : <X className="h-3.5 w-3.5 text-slate-400" />}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11"
            data-testid="platform-save-modules-btn"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </>
      ) : !selectedTenant ? (
        <div className="flex flex-col items-center justify-center h-40 text-slate-500">
          <Settings className="h-10 w-10 mb-3 text-slate-600" />
          <p>Select a school to manage its modules</p>
        </div>
      ) : null}
    </div>
  );
};

export default PlatformModuleConfig;
